import type { Bidder } from '../data/mockLots';

type Props = {
  bidder: Bidder;
  size?: 'xs' | 'sm' | 'md';
  showCount?: boolean;
  showName?: boolean;
};

const sizes = {
  xs: { dot: 18, gap: 'gap-1.5', text: 'text-[10px]', name: 'text-[10px]' },
  sm: { dot: 22, gap: 'gap-2', text: 'text-[11px]', name: 'text-xs' },
  md: { dot: 28, gap: 'gap-2', text: 'text-xs', name: 'text-sm' },
};

export function BidderAvatar({
  bidder,
  size = 'sm',
}: {
  bidder: Bidder;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}) {
  const s = size === 'lg' ? 36 : sizes[size].dot;
  return (
    <div
      className="relative inline-flex items-center justify-center rounded-full font-mono font-semibold text-bg shrink-0"
      style={{
        width: s,
        height: s,
        background: `linear-gradient(135deg, hsl(${bidder.hue}, 90%, 65%) 0%, hsl(${(bidder.hue + 40) % 360}, 85%, 50%) 100%)`,
        fontSize: s * 0.42,
        boxShadow: `0 0 0 1px hsl(${bidder.hue}, 60%, 30%), 0 0 ${s * 0.4}px hsla(${bidder.hue}, 80%, 50%, 0.35)`,
      }}
    >
      {bidder.initial}
    </div>
  );
}

// Escalating tone for a bidder's repeat-bid count: the more bids one person has
// stacked on a lot, the hotter the badge reads. Shared by the lot detail's
// LAST BID slot and the home hero's leader slot so they stay identical.
function repeatToneClass(count: number): string {
  return count >= 20
    ? 'text-accent border-accent/50 bg-accent/10'
    : count >= 10
      ? 'text-warning border-warning/40 bg-warning/5'
      : count >= 5
        ? 'text-primary border-primary/40 bg-primary/5'
        : count >= 2
          ? 'text-success border-success/40 bg-success/5'
          : 'text-fg-muted border-fg-dim/40 bg-fg-dim/5';
}

// ×N badge for how many bids one bidder placed on a lot. Renders nothing for a
// single bid — a "×1" carries no information.
export function RepeatBadge({ count }: { count: number }) {
  if (count <= 1) return null;
  return (
    <span
      className={`font-mono text-[10px] tabular-nums font-semibold px-1.5 py-px border leading-none ${repeatToneClass(count)}`}
    >
      ×{count}
    </span>
  );
}

export function BidderPill({ bidder, size = 'sm', showCount = true, showName = true }: Props) {
  const cfg = sizes[size];
  return (
    <div className={`inline-flex items-center ${cfg.gap}`}>
      <BidderAvatar bidder={bidder} size={size} />
      {showName && (
        <span className={`font-medium ${cfg.name} text-fg truncate max-w-[88px]`}>
          {bidder.firstName}
        </span>
      )}
      {showCount && bidder.bidCount > 1 && (
        <span
          className={`font-mono ${cfg.text} text-primary px-1 py-[1px] rounded border border-primary/30 bg-primary/5`}
        >
          ×{bidder.bidCount}
        </span>
      )}
    </div>
  );
}

export function BidderStack({ bidders, max = 4 }: { bidders: Bidder[]; max?: number }) {
  const shown = bidders.slice(0, max);
  const more = Math.max(0, bidders.length - max);
  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {shown.map((b) => (
          <div key={b.id} className="ring-2 ring-bg rounded-full">
            <BidderAvatar bidder={b} size="sm" />
          </div>
        ))}
      </div>
      {more > 0 && (
        <span className="ml-2 font-mono text-[10px] text-fg-muted tracking-wider">+{more}</span>
      )}
    </div>
  );
}
